import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CaseType } from "../../types/Case";
import { isEqual } from "lodash";
import { RootState } from "../store";
import api from "../../utils/api";

interface CasesState {
  cases: CaseType[];
  partnerCases: [];
  partnerTokens: string[];
  caseOkCases: CaseType[];
  designReadyCases: CaseType[];
  selectedCase: CaseType | null;

  totalCount: number;
  totalPages: number;
  currentPage: number;

  caseOkTotalCount: number;
  caseOkTotalPages: number;
  caseOkCurrentPage: number;

  designReadyTotalCount: number;
  designReadyTotalPages: number;
  designReadyCurrentPage: number;

  isLoading: boolean;
  error: string | null;
}

const initialState: CasesState = {
  cases: [],
  partnerCases: [],
  partnerTokens: [],
  caseOkCases: [],
  designReadyCases: [],
  selectedCase: null,

  totalCount: 0,
  totalPages: 0,
  currentPage: 1,

  caseOkTotalCount: 0,
  caseOkTotalPages: 0,
  caseOkCurrentPage: 1,

  designReadyTotalCount: 0,
  designReadyTotalPages: 0,
  designReadyCurrentPage: 1,

  isLoading: false,
  error: null,
};

interface GetCasesParams {
  page?: number;
  search?: string;
  status?: string;
}

export const getAllCases = createAsyncThunk(
  "admin/getAllCases",
  async (params: GetCasesParams, { rejectWithValue, getState }) => {
    try {
      const { page = 1, search = "", status = "" } = params;
      const response = await api.get(`/auth/admin/all-cases`, {
        params: { page, search, status },
      });

      const newData = response.data.data;
      const currentCases = (getState() as RootState).adminCases.cases;

      const isSame = isEqual(currentCases, newData?.data || []);

      return {
        ...newData,
        __skipUpdate: isSame,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cases"
      );
    }
  }
);

export const getAllPartnerCases = createAsyncThunk(
  "partnerAdmin/getAllPartnerCases",
  async (
    params: GetCasesParams & { userID: string; token?: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const { page = 1, search = "", status = "", token = "", userID } = params;

      const response = await api.get(`/api/partners/cases/${userID}`, {
        params: { page, search, status, token },
      });

      const {
        data: caseArray = [],
        totalCount = 0,
        currentPage = 1,
        totalPages = 1,
        tokens = [],
      } = response.data.data;

      const pagination = { totalCount, currentPage, totalPages };

      const currentCases = (getState() as RootState).adminCases.partnerCases;
      const isSame = isEqual(currentCases, caseArray);

      return {
        cases: caseArray,
        pagination,
        tokens,
        __skipUpdate: isSame,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner cases"
      );
    }
  }
);

export const unholdCaseAllocation = createAsyncThunk(
  "admin/unholdCaseAllocation",
  async (
    {
      case_id,
      allocation_id,
    }: { case_id: string; allocation_id?: number | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(`/auth/admin/case/${case_id}/unhold`, {
        allocation_id,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unhold allocation",
      );
    }
  },
);

export const getCasesById = createAsyncThunk(
  "admin/getCasesById",
  async (case_id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/admin/case/${case_id}`);
      return response.data.data as CaseType;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch case details"
      );
    }
  }
);

export const fetchCaseOkCases = createAsyncThunk(
  "cases/fetchCaseOkCases",
  async (
    { page = 1, limit = 50 }: { page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/auth/admin/CaseOkcases`,
        {
          params: { page, limit },
        }
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Case OK cases"
      );
    }
  }
);

export const fetchDesignReadyCases = createAsyncThunk(
  "cases/fetchDesignReadyCases",
  async (
    { page = 1, limit = 50 }: { page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/auth/admin/DesignReadycases`, {
        params: { page, limit },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Design Ready cases"
      );
    }
  }
);

const caseSlice = createSlice({
  name: "adminCases",
  initialState,
  reducers: {
    clearSelectedCase: (state) => {
      state.selectedCase = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllCases.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllCases.fulfilled, (state, action) => {
        state.isLoading = false;
        // always update pagination
        state.totalCount = action.payload.totalCount || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.currentPage = action.payload.currentPage || 1;
        // update cases only if changed
        if (!action.payload.__skipUpdate) {
          state.cases = action.payload.data || [];
        }
      })
      .addCase(getAllCases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getAllPartnerCases.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllPartnerCases.fulfilled, (state, action) => {
        state.isLoading = false;
        // always update pagination
        state.totalCount = action.payload.pagination.totalCount || 0;
        state.totalPages = action.payload.pagination.totalPages || 0;
        state.currentPage = action.payload.pagination.currentPage || 1;
        // update cases only if changed
        if (!action.payload.__skipUpdate) {
          state.partnerCases = action.payload.cases || [];
        }
        state.partnerTokens = action.payload.tokens || [];
      })
      .addCase(getAllPartnerCases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getCasesById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCasesById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedCase = action.payload;
      })
      .addCase(getCasesById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchCaseOkCases.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCaseOkCases.fulfilled, (state, action) => {
        state.isLoading = false;
        state.caseOkCases = action.payload.data || [];
        state.caseOkTotalCount = action.payload.totalCount || 0;
        state.caseOkTotalPages = action.payload.totalPages || 0;
        state.caseOkCurrentPage = action.payload.currentPage || 1;
      })
      .addCase(fetchCaseOkCases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchDesignReadyCases.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDesignReadyCases.fulfilled, (state, action) => {
        state.isLoading = false;
        state.designReadyCases = action.payload.data || [];
        state.designReadyTotalCount = action.payload.totalCount || 0;
        state.designReadyTotalPages = action.payload.totalPages || 0;
        state.designReadyCurrentPage = action.payload.currentPage || 1;
      })
      .addCase(fetchDesignReadyCases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedCase } = caseSlice.actions;
export default caseSlice.reducer;
